import type { ReactElement } from 'react';
import { useMemo, useCallback } from 'react';
import { DataTable } from '@/components/DataTable';
import type { DataFetchParams } from '@/components/DataTable';
import type { PersonalInstalledToolListItem } from '@kirin/api-client/contracts/account-shadow-ai-installed-tools';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SHADOW_AI_REFRESH_INTERVAL } from '@/hooks/api/useShadowAiTools';
import { queryKeys } from '@/hooks/api/queryKeys';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TOOL_APPROVAL_SCOPES,
  TOOL_APPROVAL_TABS,
  type ToolApprovalFilter,
} from '@kirin/api-client/contracts/shadow-ai-tool-approvals';
import { usePersonalToolApprovalTable } from '@/components/shared/shadow-ai/usePersonalToolApprovalTable';
import { createToolApprovalColumn } from '@/components/shared/shadow-ai/toolApprovalColumn';
import { createPersonalInstalledToolsColumns } from './columns';
import {
  fetchPersonalInstalledTools,
  type PersonalInstalledToolsMeta,
} from './api';
import {
  getInstalledToolRowId,
  SHADOW_AI_TABLE_SCROLL_CLASS,
} from '@/components/shared/shadow-ai';
import { ExpandedToolRow } from './ExpandedToolRow';
import { PERSONAL_INSTALLED_TOOLS_TABLE_CONFIG } from './constants';

function getQueryKeysFromParams(
  params: DataFetchParams,
  approval?: ToolApprovalFilter
) {
  return queryKeys.shadowAi.personalInstalledAiToolsDataTable({
    page: params.pagination.pageIndex + 1,
    limit: params.pagination.pageSize,
    filters: params.filters,
    sortBy: params.sort?.field,
    sortOrder: params.sort?.order,
    search: params.search,
    approval,
  });
}

interface DataTablePersonalInstalledToolsContentProps {
  onMeta?: (meta: PersonalInstalledToolsMeta) => void;
}

export function DataTablePersonalInstalledToolsContent({
  onMeta,
}: DataTablePersonalInstalledToolsContentProps): ReactElement {
  // KIR-4864: the owner approves on their personal account + Show filter.
  const approvalTable = usePersonalToolApprovalTable({
    tableId: PERSONAL_INSTALLED_TOOLS_TABLE_CONFIG.tableId,
    tab: TOOL_APPROVAL_TABS.INSTALLED,
    countsRefetchInterval: SHADOW_AI_REFRESH_INTERVAL,
  });
  const { approvalFilter } = approvalTable;
  const showApproval = approvalTable.enabled;

  const columns = useMemo(() => {
    const base = createPersonalInstalledToolsColumns();
    if (!showApproval) return base;
    return [
      ...base,
      createToolApprovalColumn<PersonalInstalledToolListItem>({
        getToolId: row => row.toolId,
        scope: TOOL_APPROVAL_SCOPES.ENDPOINT,
        personal: true,
      }),
    ];
  }, [showApproval]);

  const queryFn = useCallback(
    (params: DataFetchParams, signal?: AbortSignal) =>
      fetchPersonalInstalledTools(params, signal, approvalFilter),
    [approvalFilter]
  );

  const queryKeyFn = useCallback(
    (params: DataFetchParams) => getQueryKeysFromParams(params, approvalFilter),
    [approvalFilter]
  );

  const queryConfig = useMemo(
    () => ({
      staleTime: 30_000,
      refetchInterval: SHADOW_AI_REFRESH_INTERVAL,
      refetchOnWindowFocus: false,
    }),
    []
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Installed AI Tools</CardTitle>
      </CardHeader>
      <CardContent>
        {approvalTable.isFlagLoading ? (
          <div data-testid="tool-approval-flag-loading">
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <DataTable<PersonalInstalledToolListItem, PersonalInstalledToolsMeta>
            id={PERSONAL_INSTALLED_TOOLS_TABLE_CONFIG.tableId}
            mode="server"
            className="flex flex-col"
            containerClassName={SHADOW_AI_TABLE_SCROLL_CLASS}
            columns={columns}
            errorBoundaryLabel="Something went wrong in the Installed AI Tools table"
            search={{
              placeholder: 'Search installed AI tools...',
              className: 'w-[260px]',
            }}
            features={{ enableSorting: true, enableRowExpansion: true }}
            rowExpansion={{
              getRowId: getInstalledToolRowId,
              render: row => <ExpandedToolRow row={row} />,
              contentClassName: 'bg-muted/30 p-4',
              rowClassName: 'bg-muted/10',
              keepMounted: false,
              showContainer: false,
              highlightOnExpand: true,
              scrollToExpandedRow: true,
            }}
            queryFn={queryFn}
            getQueryKeysFromParams={queryKeyFn}
            onMeta={onMeta}
            noDataLabel="No installed AI tools found."
            queryConfig={queryConfig}
            {...approvalTable.dataTableProps}
          />
        )}
      </CardContent>
    </Card>
  );
}

DataTablePersonalInstalledToolsContent.displayName =
  'DataTablePersonalInstalledToolsContent';
